import React, { createElement, useCallback, useEffect, useRef, useState } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
} from 'react-native';
import DateTimePicker, { DateTimePickerEvent } from '@react-native-community/datetimepicker';
import type { MaterialTopTabNavigationProp } from '@react-navigation/material-top-tabs';
import { CommonActions, useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { LottieOverlayBurst } from '../../components/LottieOverlayBurst';
import { EventsListSkeleton } from '../../components/skeletons/ScreenSkeletons';
import { SearchLocationBanner } from '../../components/SearchLocationBanner';
import { SearchLocationPickerModal } from '../../components/SearchLocationPickerModal';
import { ModalBottomSheet } from '../../components/ModalBottomSheet';
import { useAuth } from '../../contexts/AuthContext';
import { showGuestRegisterPrompt } from '../../utils/guestReadOnlyAlert';
import { useSearchLocationFilter } from '../../hooks/useSearchLocationFilter';
import { notifyEventUpdatedToParticipants } from '../../services/eventUpdateNotifications';
import {
  createEvent,
  getEvents,
  joinEvent,
  leaveEvent,
  updateEventDetails,
} from '../../services/communityService';
import { getBlockedUserIdsSet } from '../../services/userService';
import {
  EVENT_CATEGORIES,
  type CommunityEventDoc,
  type EventCategory,
} from '../../types/community';
import type { CommunityTopTabParamList } from '../../navigation/types';
import { impactLight } from '../../utils/haptics';

function formatEventWhen(eventAt: CommunityEventDoc['eventAt']): string {
  try {
    const d = eventAt.toDate();
    return d.toLocaleString('tr-TR', {
      weekday: 'short',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function defaultEventDate(): Date {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  return d;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function toDateInputValue(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function dateInputMin(): string {
  return toDateInputValue(new Date());
}

function formatPickerDate(d: Date): string {
  return d.toLocaleString('tr-TR', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

const EVENT_ROW_LAYOUT_LENGTH = 216;

export function EventsScreen() {
  const insets = useSafeAreaInsets();
  const rootNavigation = useNavigation();
  const communityNav = useNavigation<MaterialTopTabNavigationProp<CommunityTopTabParamList>>();
  const route = useRoute<RouteProp<CommunityTopTabParamList, 'CommunityEvents'>>();
  const focusEventId = route.params?.focusEventId;
  const eventsListRef = useRef<FlatList<CommunityEventDoc>>(null);
  const { user, profile, logout } = useAuth();
  const isGuest = Boolean(user?.isAnonymous);
  const guestLocationPromptedRef = useRef(false);
  const {
    profileCity,
    profileDistrict,
    effectiveCity,
    effectiveDistrict,
    canSearch,
    isTemporaryFilter,
    pickerOpen,
    setPickerOpen,
    draftCity,
    setDraftCity,
    draftDistrict,
    setDraftDistrict,
    openPicker,
    applyPicker,
    clearFilter,
  } = useSearchLocationFilter(profile);

  useFocusEffect(
    useCallback(() => {
      if (isGuest && !guestLocationPromptedRef.current) {
        guestLocationPromptedRef.current = true;
        setPickerOpen(true);
      }
    }, [isGuest, setPickerOpen]),
  );

  const [events, setEvents] = useState<CommunityEventDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [joiningId, setJoiningId] = useState<string | null>(null);
  const [leavingId, setLeavingId] = useState<string | null>(null);
  const [confettiBurst, setConfettiBurst] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [eventDate, setEventDate] = useState<Date>(defaultEventDate);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [draftVenue, setDraftVenue] = useState('');
  const [draftCategory, setDraftCategory] = useState<EventCategory>('Park buluşması');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CommunityEventDoc | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editVenue, setEditVenue] = useState('');
  const [editEventDate, setEditEventDate] = useState<Date>(defaultEventDate);
  const [editShowDatePicker, setEditShowDatePicker] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const canCreateEvent = Boolean(profileCity?.trim());

  useEffect(() => {
    if (modalOpen) {
      setEventDate(defaultEventDate());
    }
  }, [modalOpen]);

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!canSearch) {
        setEvents([]);
        setLoading(false);
        setRefreshing(false);
        return;
      }
      if (!opts?.silent) {
        setLoading(true);
      }
      try {
        const list = await getEvents(
          effectiveCity,
          effectiveDistrict.trim() ? effectiveDistrict : undefined,
        );
        const blocked = getBlockedUserIdsSet(profile);
        setEvents(list.filter(e => !blocked.has(e.createdBy)));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [canSearch, effectiveCity, effectiveDistrict, profile],
  );

  useEffect(() => {
    void load({ silent: true });
  }, [load]);

  useEffect(() => {
    if (!focusEventId || events.length === 0) {
      return;
    }
    const i = events.findIndex(e => e.id === focusEventId);
    if (i < 0) {
      communityNav.setParams({ focusEventId: undefined });
      return;
    }
    const scroll = () => {
      try {
        eventsListRef.current?.scrollToIndex({
          index: i,
          animated: true,
          viewPosition: 0.12,
        });
      } catch {
        eventsListRef.current?.scrollToOffset({
          offset: Math.max(0, EVENT_ROW_LAYOUT_LENGTH * i - 80),
          animated: true,
        });
      }
    };
    requestAnimationFrame(scroll);
    const t = setTimeout(() => {
      communityNav.setParams({ focusEventId: undefined });
    }, 900);
    return () => clearTimeout(t);
  }, [focusEventId, events, communityNav]);

  async function onSubmitEvent() {
    setFormError(null);
    if (!user?.uid || !canCreateEvent) {
      setFormError('Profilinde il bilgisi gerekli.');
      return;
    }
    const title = draftTitle.trim();
    const venue = draftVenue.trim();
    if (!title || !venue) {
      setFormError('Başlık ve mekan adı zorunlu.');
      return;
    }
    if (eventDate.getTime() < Date.now() - 60_000) {
      setFormError('Etkinlik tarihi geçmiş olamaz.');
      return;
    }
    setSubmitting(true);
    try {
      await createEvent(user.uid, {
        title,
        venueName: venue,
        category: draftCategory,
        city: profileCity,
        district: profileDistrict,
        eventAt: eventDate,
      });
      setModalOpen(false);
      setDraftTitle('');
      setDraftVenue('');
      setDraftCategory('Park buluşması');
      await load({ silent: true });
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : 'Kaydedilemedi.');
    } finally {
      setSubmitting(false);
    }
  }

  async function onJoin(eventId: string) {
    if (isGuest) {
      showGuestRegisterPrompt(logout);
      return;
    }
    if (!user?.uid) {
      return;
    }
    const ev = events.find(e => e.id === eventId);
    if (ev?.participants.includes(user.uid)) {
      return;
    }
    void impactLight();
    setJoiningId(eventId);
    try {
      await joinEvent(eventId, user.uid);
      setConfettiBurst(true);
      setEvents(prev =>
        prev.map(e =>
          e.id === eventId ? { ...e, participants: [...e.participants, user.uid] } : e,
        ),
      );
    } catch {
      await load({ silent: true });
    } finally {
      setJoiningId(null);
    }
  }

  async function onLeave(eventId: string) {
    if (isGuest || !user?.uid) {
      return;
    }
    const ev = events.find(e => e.id === eventId);
    if (!ev || ev.createdBy === user.uid || !ev.participants.includes(user.uid)) {
      return;
    }
    setLeavingId(eventId);
    try {
      await leaveEvent(eventId, user.uid);
      setEvents(prev =>
        prev.map(e =>
          e.id === eventId
            ? { ...e, participants: e.participants.filter(id => id !== user.uid) }
            : e,
        ),
      );
    } catch {
      await load({ silent: true });
    } finally {
      setLeavingId(null);
    }
  }

  function openEditModal(ev: CommunityEventDoc) {
    setEditingEvent(ev);
    setEditTitle(ev.title);
    setEditVenue(ev.venueName);
    try {
      setEditEventDate(ev.eventAt.toDate());
    } catch {
      setEditEventDate(defaultEventDate());
    }
    setEditError(null);
    setEditModalOpen(true);
  }

  async function onSubmitEdit() {
    if (!user?.uid || !editingEvent) {
      return;
    }
    const title = editTitle.trim();
    const venue = editVenue.trim();
    if (!title || !venue) {
      setEditError('Başlık ve mekan zorunlu.');
      return;
    }
    if (editEventDate.getTime() < Date.now() - 60_000) {
      setEditError('Etkinlik tarihi geçmiş olamaz.');
      return;
    }
    setEditSubmitting(true);
    setEditError(null);
    try {
      const prevOthers = editingEvent.participants.filter(id => id !== editingEvent.createdBy);
      await updateEventDetails(editingEvent.id, user.uid, {
        title,
        venueName: venue,
        eventAt: editEventDate,
      });
      notifyEventUpdatedToParticipants(title, prevOthers.length);
      setEditModalOpen(false);
      setEditingEvent(null);
      await load({ silent: true });
    } catch (e: unknown) {
      setEditError(e instanceof Error ? e.message : 'Güncellenemedi.');
    } finally {
      setEditSubmitting(false);
    }
  }

  function onNativeDateChange(event: DateTimePickerEvent, date?: Date) {
    if (Platform.OS === 'android') {
      setShowDatePicker(false);
    }
    if (event.type === 'dismissed') {
      if (Platform.OS === 'ios') {
        setShowDatePicker(false);
      }
      return;
    }
    if (date) {
      const nd = new Date(date);
      nd.setHours(10, 0, 0, 0);
      setEventDate(nd);
    }
    if (Platform.OS === 'ios') {
      setShowDatePicker(false);
    }
  }

  function renderEmpty() {
    if (!canSearch) {
      return (
 <View className="rounded-2xl border border-zinc-200 bg-white p-6 ">
 <Text className="text-center text-sm leading-6 text-gray-600">
            Listeyi görmek için yukarıdan{' '}
 <Text className="font-semibold">{isGuest ? 'Konum Seç' : 'Konum Değiştir'}</Text> ile en az il
            seçebilirsin
            {isGuest
              ? '.'
              : ' veya Ana Sayfa → Profilim’den kalıcı adresini kaydedebilirsin.'}
          </Text>
        </View>
      );
    }
    if (isTemporaryFilter) {
      return (
 <View className="rounded-2xl border border-zinc-200 bg-zinc-50 dark:bg-zinc-900 px-5 py-8 ">
 <Text className="text-center text-base font-semibold text-gray-800">
            Seçtiğin bu bölgede henüz yaklaşan etkinlik yok.
          </Text>
 <Text className="mt-2 text-center text-sm leading-6 text-gray-600">
            Kendi bölgene dönmek ister misin? Aşağıdaki düğme profil konumuna göre listeyi yeniler.
          </Text>
          <TouchableOpacity
            activeOpacity={0.88}
            onPress={clearFilter}
            className="mt-5 self-center rounded-xl bg-zinc-900 px-6 py-2.5 dark:bg-zinc-50">
 <Text className="text-center text-sm font-bold text-white">
              {isGuest ? 'Konum seçimini temizle' : 'Profil konumuma dön'}
            </Text>
          </TouchableOpacity>
        </View>
      );
    }
    return (
 <View className="rounded-2xl border border-zinc-200 bg-zinc-50 dark:bg-zinc-900 px-5 py-8 ">
 <Text className="text-center text-base font-semibold text-gray-800">
          Bu bölgede yaklaşan etkinlik yok
        </Text>
 <Text className="mt-2 text-center text-xs leading-5 text-gray-500">
          İlk buluşmayı sen düzenleyebilirsin — sağ alttaki + düğmesine dokun.
        </Text>
      </View>
    );
  }

  if (loading && events.length === 0 && canSearch) {
    return (
 <View className="flex-1 bg-white dark:bg-black">
        <EventsListSkeleton />
      </View>
    );
  }

  return (
 <View className="flex-1 bg-white dark:bg-black">
      <LottieOverlayBurst
        visible={confettiBurst}
        variant="confetti"
        onFinish={() => setConfettiBurst(false)}
      />
      <FlatList
        ref={eventsListRef}
        data={events}
        keyExtractor={item => item.id}
        getItemLayout={(_, index) => ({
          length: EVENT_ROW_LAYOUT_LENGTH,
          offset: EVENT_ROW_LAYOUT_LENGTH * index,
          index,
        })}
        onScrollToIndexFailed={info => {
          const offset = info.averageItemLength * info.index;
          eventsListRef.current?.scrollToOffset({ offset, animated: true });
        }}
        contentContainerStyle={{
          paddingHorizontal: 20,
          paddingTop: 16,
          paddingBottom: 40 + insets.bottom + 112,
        }}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={() => {
              setRefreshing(true);
              void load({ silent: true });
            }}
            tintColor="#18181b"
          />
        }
        ListHeaderComponent={
 <View className="mb-4">
            <SearchLocationBanner
              theme="orange"
              effectiveCity={effectiveCity}
              effectiveDistrict={effectiveDistrict}
              isTemporaryFilter={isTemporaryFilter}
              onChangePress={openPicker}
              onClearFilter={clearFilter}
              isGuest={isGuest}
            />
            <TouchableOpacity
              activeOpacity={0.9}
              onPress={() => {
                void impactLight();
                rootNavigation.dispatch(CommonActions.navigate({ name: 'Playdates' }));
              }}
              className="mt-4 overflow-hidden rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900">
 <View className="flex-row items-center justify-between gap-3">
 <View className="min-w-0 flex-1">
 <Text className="text-sm font-bold text-zinc-900 dark:text-zinc-100">
                    Oyun Arkadaşı Bul
                  </Text>
 <Text className="mt-0.5 text-xs text-zinc-600 dark:text-zinc-500">
                    Yaşıt bebekli anneler — aynı ilçe
                  </Text>
                </View>
 <Text className="text-xl">🧸</Text>
              </View>
            </TouchableOpacity>
          </View>
        }
        ListEmptyComponent={renderEmpty}
        renderItem={({ item }) => {
          const uid = user?.uid;
          const isCreator = Boolean(uid && item.createdBy === uid);
          const joined = Boolean(uid && item.participants.includes(uid));
          const count = item.participants.length;
          return (
            <View className="mb-4 rounded-xl border border-zinc-200 bg-white px-4 py-3.5 dark:border-zinc-800 dark:bg-zinc-900">
 <View className="mb-2 flex-row items-start justify-between gap-2">
 <Text className="flex-1 text-lg font-semibold text-gray-900">{item.title}</Text>
 <View className="rounded-full bg-zinc-50 dark:bg-zinc-900 px-2 py-0.5">
 <Text className="text-xs font-medium text-zinc-700">{item.category}</Text>
                </View>
              </View>
 <Text className="mb-1 text-sm text-gray-600">{formatEventWhen(item.eventAt)}</Text>
 <Text className="mb-3 text-sm text-gray-700">
 <Text className="font-medium text-gray-500">Mekan: </Text>
                {item.venueName}
              </Text>
 <View className="flex-row flex-wrap items-center justify-between gap-2">
 <Text className="text-xs text-gray-400">{count} katılımcı</Text>
 <View className="flex-row flex-wrap items-center justify-end gap-2">
                  {uid && isCreator ? (
                    <TouchableOpacity
                      activeOpacity={0.88}
                      onPress={() => openEditModal(item)}
                      className="rounded-lg border border-zinc-300 bg-zinc-50 px-3 py-1.5 dark:bg-zinc-900">
                        <Text className="text-xs font-semibold text-zinc-900 dark:text-zinc-200">
                          Düzenle
                        </Text>
                    </TouchableOpacity>
                  ) : null}
                  {!isCreator && uid ? (
                    joined ? (
                      <TouchableOpacity
                        disabled={leavingId === item.id}
                        onPress={() => void onLeave(item.id)}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 active:opacity-90">
                        {leavingId === item.id ? (
                          <ActivityIndicator color="#6b7280" size="small" />
                        ) : (
                          <Text className="text-xs font-semibold text-gray-600">
                            Katılımdan Vazgeç
                          </Text>
                        )}
                      </TouchableOpacity>
                    ) : (
                      <TouchableOpacity
                        disabled={joiningId === item.id}
                        onPress={() => void onJoin(item.id)}
                        className="rounded-lg border border-zinc-900 bg-zinc-900 px-3 py-1.5 active:opacity-90 dark:border-zinc-50 dark:bg-zinc-50">
                        {joiningId === item.id ? (
                          <ActivityIndicator color="#fff" size="small" />
                        ) : (
                          <Text className="text-xs font-semibold text-white dark:text-zinc-950">Katıl</Text>
                        )}
                      </TouchableOpacity>
                    )
                  ) : null}
                </View>
              </View>
            </View>
          );
        }}
      />

      <ModalBottomSheet
        visible={modalOpen}
        onRequestClose={() => !submitting && setModalOpen(false)}
        dismissDisabled={submitting}
        keyboardAvoiding>
        <View
          className="rounded-t-3xl border border-zinc-200 bg-white px-5 pt-4"
          style={{ paddingBottom: 24 + insets.bottom }}>
 <View className="mb-4 h-1 w-10 self-center rounded-full bg-zinc-200 dark:bg-zinc-800" />
 <Text className="mb-4 text-lg font-bold text-gray-900">Yeni buluşma</Text>

            <Text className="mb-1 text-xs font-medium text-gray-500">Başlık</Text>
            <TextInput
              value={draftTitle}
              onChangeText={setDraftTitle}
              placeholder="Örn. Kadıköy anneleri park buluşması"
              placeholderTextColor="#9ca3af"
              className="mb-3 border-b border-zinc-200 py-2 text-base text-gray-900 dark:border-zinc-700 dark:text-zinc-100"
            />

 <Text className="mb-1 text-xs font-medium text-gray-500">Tarih</Text>
            {Platform.OS === 'web' ? (
 <View className="mb-3 overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-50 dark:bg-zinc-900/50 px-2 py-1">
                {createElement('input', {
                  type: 'date',
                  value: toDateInputValue(eventDate),
                  min: dateInputMin(),
                  onChange: (e: { target: { value: string } }) => {
                    const v = e.target.value;
                    if (v) {
                      const [y, mo, da] = v.split('-').map(Number);
                      const nd = new Date(y, mo - 1, da, 10, 0, 0);
                      setEventDate(nd);
                    }
                  },
                  style: {
                    width: '100%',
                    padding: 12,
                    borderWidth: 0,
                    fontSize: 16,
                    color: '#111827',
                    backgroundColor: 'transparent',
                  },
                })}
              </View>
            ) : (
 <View className="mb-3">
                <TouchableOpacity
                  onPress={() => setShowDatePicker(true)}
                  activeOpacity={0.88}
                  className="border-b border-zinc-200 py-2 dark:border-zinc-700">
                  <Text className="text-base text-gray-800 dark:text-zinc-100">
                    {formatPickerDate(eventDate)}
                  </Text>
 <Text className="mt-0.5 text-xs text-gray-500">Tarih seçmek için dokun</Text>
                </TouchableOpacity>
                {showDatePicker ? (
                  <DateTimePicker
                    value={eventDate}
                    mode="date"
                    display="default"
                    minimumDate={new Date()}
                    onChange={onNativeDateChange}
                  />
                ) : null}
              </View>
            )}

 <Text className="mb-1 text-xs font-medium text-gray-500">Park / mekan adı</Text>
            <TextInput
              value={draftVenue}
              onChangeText={setDraftVenue}
              placeholder="Örn. Moda Sahil Parkı"
              placeholderTextColor="#9ca3af"
              className="mb-3 border-b border-zinc-200 py-2 text-base text-gray-900 dark:border-zinc-700 dark:text-zinc-100"
            />

            <Text className="mb-2 text-xs font-medium text-gray-500">Kategori</Text>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              className="mb-4 border-b border-zinc-200 dark:border-zinc-700">
              <View className="flex-row">
                {EVENT_CATEGORIES.map(cat => {
                  const selected = draftCategory === cat;
                  return (
                    <TouchableOpacity
                      key={cat}
                      onPress={() => setDraftCategory(cat)}
                      className={`mr-4 pb-2 ${
                        selected
                          ? 'border-b-2 border-zinc-900 dark:border-zinc-100'
                          : 'border-b-2 border-transparent'
                      }`}>
                      <Text
                        className={`text-xs font-semibold ${
                          selected
                            ? 'text-zinc-900 dark:text-zinc-100'
                            : 'text-zinc-500 dark:text-zinc-500'
                        }`}>
                        {cat}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </ScrollView>

            {formError ? (
 <Text className="mb-3 text-sm text-zinc-700">{formError}</Text>
            ) : null}

            <TouchableOpacity
              disabled={submitting}
              onPress={() => void onSubmitEvent()}
              className="rounded-xl bg-zinc-900 py-3 active:opacity-90 dark:bg-zinc-50">
              {submitting ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text className="text-center text-sm font-semibold text-white dark:text-zinc-950">
                  Kaydet
                </Text>
              )}
            </TouchableOpacity>
          </View>
      </ModalBottomSheet>

      <ModalBottomSheet
        visible={editModalOpen}
        onRequestClose={() => !editSubmitting && setEditModalOpen(false)}
        dismissDisabled={editSubmitting}
        keyboardAvoiding>
        <View
          className="rounded-t-3xl border border-zinc-200 bg-white px-5 pt-4"
          style={{ paddingBottom: 24 + insets.bottom }}>
 <View className="mb-4 h-1 w-10 self-center rounded-full bg-zinc-200 dark:bg-zinc-800" />
 <Text className="mb-4 text-lg font-bold text-gray-900">Buluşmayı düzenle</Text>

 <Text className="mb-1 text-xs font-medium text-gray-500">Başlık</Text>
            <TextInput
              value={editTitle}
              onChangeText={setEditTitle}
              placeholder="Başlık"
              placeholderTextColor="#9ca3af"
              className="mb-3 border-b border-zinc-200 py-2 text-base text-gray-900 dark:border-zinc-700 dark:text-zinc-100"
            />

            <Text className="mb-1 text-xs font-medium text-gray-500">Tarih</Text>
            {Platform.OS === 'web' ? (
              <View className="mb-3 overflow-hidden rounded-lg border border-zinc-200 bg-zinc-50 px-2 py-1 dark:border-zinc-700 dark:bg-zinc-900/50">
                {createElement('input', {
                  type: 'date',
                  value: toDateInputValue(editEventDate),
                  min: dateInputMin(),
                  onChange: (e: { target: { value: string } }) => {
                    const v = e.target.value;
                    if (v) {
                      const [y, mo, da] = v.split('-').map(Number);
                      setEditEventDate(new Date(y, mo - 1, da, 10, 0, 0));
                    }
                  },
                  style: {
                    width: '100%',
                    padding: 12,
                    borderWidth: 0,
                    fontSize: 16,
                    color: '#111827',
                    backgroundColor: 'transparent',
                  },
                })}
              </View>
            ) : (
 <View className="mb-3">
                <TouchableOpacity
                  onPress={() => setEditShowDatePicker(true)}
                  activeOpacity={0.88}
                  className="border-b border-zinc-200 py-2 dark:border-zinc-700">
                  <Text className="text-base text-gray-800 dark:text-zinc-100">
                    {formatPickerDate(editEventDate)}
                  </Text>
                </TouchableOpacity>
                {editShowDatePicker ? (
                  <DateTimePicker
                    value={editEventDate}
                    mode="date"
                    display="default"
                    minimumDate={new Date()}
                    onChange={(ev, d) => {
                      if (Platform.OS === 'android') {
                        setEditShowDatePicker(false);
                      }
                      if (ev.type === 'dismissed') {
                        if (Platform.OS === 'ios') {
                          setEditShowDatePicker(false);
                        }
                        return;
                      }
                      if (d) {
                        const nd = new Date(d);
                        nd.setHours(10, 0, 0, 0);
                        setEditEventDate(nd);
                      }
                      if (Platform.OS === 'ios') {
                        setEditShowDatePicker(false);
                      }
                    }}
                  />
                ) : null}
              </View>
            )}

 <Text className="mb-1 text-xs font-medium text-gray-500">Mekan</Text>
            <TextInput
              value={editVenue}
              onChangeText={setEditVenue}
              placeholder="Mekan adı"
              placeholderTextColor="#9ca3af"
              className="mb-3 border-b border-zinc-200 py-2 text-base text-gray-900 dark:border-zinc-700 dark:text-zinc-100"
            />

            {editError ? <Text className="mb-3 text-sm text-zinc-700">{editError}</Text> : null}

            <TouchableOpacity
              disabled={editSubmitting}
              onPress={() => void onSubmitEdit()}
              className="rounded-xl bg-zinc-900 py-3 active:opacity-90 dark:bg-zinc-50">
              {editSubmitting ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text className="text-center text-sm font-semibold text-white dark:text-zinc-950">
                  Kaydet
                </Text>
              )}
            </TouchableOpacity>
          </View>
      </ModalBottomSheet>

      <SearchLocationPickerModal
        visible={pickerOpen}
        theme="orange"
        title="Arama konumu"
        city={draftCity}
        district={draftDistrict}
        onCityChange={setDraftCity}
        onDistrictChange={setDraftDistrict}
        onApply={applyPicker}
        onClose={() => setPickerOpen(false)}
      />

      <TouchableOpacity
        activeOpacity={0.92}
        accessibilityLabel="Yeni buluşma düzenle"
        onPress={() => {
          if (isGuest) {
            showGuestRegisterPrompt(logout);
            return;
          }
          setFormError(null);
          setModalOpen(true);
        }}
        className="absolute h-14 w-14 items-center justify-center rounded-full border border-zinc-200 bg-zinc-900 dark:border-zinc-700 dark:bg-zinc-50"
        style={{
          bottom: 20 + insets.bottom,
          right: 16,
        }}>
        <Text className="text-2xl font-light text-white dark:text-zinc-900">+</Text>
      </TouchableOpacity>
    </View>
  );
}
